import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Loader from '../../../../../services/loader';
import Notifications from '../../../../../services/notifications';
import ModalHeader from '../../../../../commonComponents/modal/ModalHeader';
import TextFilter from '../../../../../commonComponents/lists/filters/TextFilter';
import ScrollContainer from '../../../../../commonComponents/container/ScrollContainer';
import ModalFooter from '../../../../../commonComponents/modal/ModalFooter';
import translate from '../../../../../commonComponents/translate';
import Modal from '../../../../../commonComponents/modal/Modal';
import { compose } from '../../../../../services/utilityFunctions';
import ProductProp from '../../../propTypes/productProp';
import PackItem from './PackItem';
import UserProp from '../../../../../commonComponents/propTypes/userProp';
import ProductVariationsProp from '../../../propTypes/productVariationsProp';
import MarketInfosContext from '../../../../../contextProviders/MarketInfosContext';

function AddPackProduct(props) {
  const { t, product, endpoint, user, onClose, selectedVariations } = props;
  const { _id: productId, pack = {} } = product || {};
  const { amountVariationsPerAdvancedPack } = pack || {};
  const { marketInfos } = useContext(MarketInfosContext);
  const currency = user?.currency || marketInfos.localCurrency;

  // Loader ref
  const loader = useRef(new Loader('#product-list'));

  // State so we know if it's needed to get more items
  const [metaInfoTotal, setMetaInfoTotal] = useState(null);
  const [page, setPage] = useState(1);

  // All products to display to the user
  const [allVariations, setAllVariations] = useState([]);
  // Filters state
  const [filters, setFilters] = useState({});
  const updateQuantity = useCallback(
    productVariationId => qty => {
      setAllVariations(prevVariations =>
        prevVariations?.map(variation =>
          variation?._id === productVariationId
            ? {
                ...variation,
                quantity: qty,
                inside:
                  (variation?.quantity || 0) < qty
                    ? (variation?.quantity || 0) < qty
                    : variation?.inside,
              }
            : variation,
        ),
      );
    },
    [setAllVariations],
  );

  // region Handles popup exit
  /**
   * Handles the "exit" of the popup, can be with or without products
   * @products {MarketProductVariations[]} - variations to add
   */
  const handleClose = (action, products) => {
    if (action === 'add') {
      const productsInside = products?.filter(p => p.inside && p.quantity > 0);
      if (productsInside?.length === 0) {
        Notifications.showNotificationWarning(
          t('warning'),
          t('advancedPack.error.amountVariationsPerAdvancedPack.product'),
        );
        return;
      }

      const numberOfProducts =
        productsInside?.reduce(
          (acc, item) => acc + parseInt(item.quantity, 10),
          0,
        ) || 0;

      if (amountVariationsPerAdvancedPack !== numberOfProducts) {
        Notifications.showNotificationWarning(
          t('warning'),
          t(
            'advancedPack.error.amountVariationsPerAdvancedPack',
            amountVariationsPerAdvancedPack,
          ),
        );
        return;
      }
      onClose(productsInside);
    } else {
      onClose(null);
    }
  };
  // endregion

  const disabledSave = useMemo(
    () =>
      amountVariationsPerAdvancedPack !==
      (allVariations?.reduce(
        (acc, item) => acc + parseInt(item.quantity || '0', 10),
        0,
      ) || 0),
    [amountVariationsPerAdvancedPack, allVariations],
  );

  // region Handles the checkbox click
  /**
   * Handles the event on the checkbox
   * Updates the `inside` property for a list of product variations.
   *
   * @param {Object} variation - The product variation to update.
   * @param {string} variation._id - The unique identifier of the variation to update.
   * @param {boolean} [variation.inside] - The current state of the `inside` property (optional).
   *
   * @callback
   */
  const addProduct = useCallback(
    variation => {
      setAllVariations(prevState =>
        prevState.map(pVariation => {
          if (pVariation._id === variation._id) {
            return { ...pVariation, inside: !pVariation?.inside };
          }
          return pVariation;
        }),
      );
    },
    [setAllVariations],
  );
  // endregion Handles the checkbox click

  // region Handles Search Change with debounce
  /**
   * Handles search input with timeout
   * @value {string}
   */
  // Create a ref to store the timeout ID
  const debounceTimeoutRef = useRef(null);
  const handleSearchChange = useCallback(
    value => {
      // Clear the previous timeout if it exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout with a delay of 400ms
      debounceTimeoutRef.current = setTimeout(() => {
        setFilters(prevState => ({
          ...prevState,
          search: value,
        }));
      }, 400);
    },
    [setFilters],
  );
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    [],
  );
  // endregion Handles Search Change with debounce

  // region HANDLE SCROLLABLE PRODUCTS
  /**
   * Request to API to Get ITEMS
   * @type {(function(number=, {}=): Promise<*|[]|undefined>)|*}
   */
  const fetchItems = async (
    filtersParam = {},
    pageNumber = 1,
    initVariations = [],
  ) => {
    try {
      loader.current.show();
      const url =
        endpoint ||
        `/table/market/product/${productId}/available/variations/list`;

      const params = filtersParam;

      const userCountry = user?.shippingCountry || user?.address?.countryCode;
      if (userCountry) {
        params.country = userCountry;
      }
      params.currency = currency;

      const { data: response } = await axios.get(url, {
        params: {
          pagination: { perpage: 10, page: pageNumber },
          ...filtersParam,
        },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      });
      const { data, meta } = response;
      return {
        pageNumber,
        newProducts: [
          ...initVariations,
          ...data.map(variation => {
            const alreadySelected = selectedVariations.find(
              pVariation => pVariation._id === variation._id,
            );
            return alreadySelected
              ? {
                  ...variation,
                  quantity: alreadySelected.quantity || 0,
                  inside: alreadySelected.inside,
                }
              : variation; // Return the original variation if not already selected
          }),
        ],

        pages: meta?.pages,
      };
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error_fetchProducts');
      return {
        pageNumber: 1,
        newProducts: [],
        pages: 1,
      };
    } finally {
      loader.current.hide();
    }
  };

  /** region Gets first batch of items for page 1 */
  useEffect(() => {
    (async () => {
      const { newProducts, pages } = await fetchItems(filters, 1, []);
      setMetaInfoTotal(pages);
      setAllVariations(newProducts);
      setPage(1);
    })();
  }, [filters]);

  /**
   * When scroll container gets to the end, we get another batch of items
   * If page is not greater that total of pages
   * And then we update the allVariation state
   * @type {(function(): Promise<void>)|*}
   */
  const isFetchingRef = useRef(false);
  const handleReachedEnd = useCallback(async () => {
    if (isFetchingRef.current) return; // Exit if a fetch is in progress

    isFetchingRef.current = true; // Set fetching status to true

    if (!metaInfoTotal || page + 1 <= metaInfoTotal) {
      const { pageNumber, newProducts, pages } = await fetchItems(
        filters,
        page + 1,
        allVariations,
      );
      setPage(pageNumber);
      setMetaInfoTotal(pages);
      setAllVariations(newProducts);
    }

    isFetchingRef.current = false; // Reset fetching status after fetch is done
  }, [
    setAllVariations,
    setPage,
    setMetaInfoTotal,
    filters,
    metaInfoTotal,
    page,
    allVariations,
  ]);
  // endregion HANDLE SCROLLABLE PRODUCTS

  return (
    <Modal id="addPackProduct">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content packModal">
          <ModalHeader
            title={t(
              'advancedPack.amountOfProducts',
              amountVariationsPerAdvancedPack,
            )}
          />
          <div className="modal-body">
            <div className="products-filters-wrapper">
              <TextFilter
                id="searchFilter"
                onChange={handleSearchChange}
                placeholder={t('filters.search.products')}
                value={filters?.search}
                isSearch
              />
            </div>
            <div id="product-list">
              <div className="content-wrapper">
                <div className="placeholder">
                  <div className="label" />
                  <span
                    className="name-wrapper"
                    style={{ paddingLeft: '1.7rem' }}
                  >
                    {t('name')}
                  </span>
                  <span className="quantity-wrapper">
                    {t('shop.product.qty')}
                  </span>
                </div>
                {allVariations?.length > 0 ? (
                  <ScrollContainer
                    onReachedEnd={handleReachedEnd}
                    onReachedEndDelay={1500}
                    containerClass="product-list-wrapper"
                  >
                    {allVariations?.map(variation => (
                      <PackItem
                        key={variation._id}
                        variation={variation}
                        onClick={addProduct}
                        onChange={updateQuantity}
                      />
                    ))}
                  </ScrollContainer>
                ) : (
                  <span className="hint-form">{t('noProducts')}</span>
                )}
              </div>
            </div>
          </div>
          <ModalFooter
            onSave={() => handleClose('add', allVariations)}
            cancelText={t('cancel')}
            onCancel={() => handleClose('cancel', null)}
            saveText={t('add.product')}
            disabledSave={disabledSave}
          />
        </div>
      </div>
    </Modal>
  );
}

AddPackProduct.propTypes = {
  t: PropTypes.func.isRequired,
  product: PropTypes.shape(ProductProp.propType).isRequired,
  endpoint: PropTypes.string,
  user: PropTypes.shape(UserProp.propType).isRequired,
  selectedVariations: PropTypes.arrayOf(
    PropTypes.shape(ProductVariationsProp.propType),
  ),
  onClose: PropTypes.func.isRequired,
};

AddPackProduct.defaultProps = {
  endpoint: null,
  selectedVariations: [],
};

export default compose(translate)(AddPackProduct);
